import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { MdReplay30, MdForward30 } from 'react-icons/md';
import Sidebar from './Sidebar';
import { Menu, VolumeIcon, ArrowLeft} from 'lucide-react';
import useFetchAudiolibro from '../hooks/useFetchAudiolibro';
import useLoadCurrentChapter from '../hooks/useLoadCurrentChapter';
import usePreloadNextChapter from '../hooks/usePreloadNextChapter';
import useAudioControls from '../hooks/useAudioControls';
import useProgressHandlers from '../hooks/useProgressHandlers';

const AudioPlayer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { audiolibroId, usuarioId } = useParams();
  const token = localStorage.getItem('token');
  const searchParams = new URLSearchParams(location.search);
  const origin = searchParams.get('origin');
  const genre = searchParams.get('genre');


  const handleGoBack = () => {
    if (origin === 'results-list') {
      navigate(`/results-list/${usuarioId}`);
    } else if (origin === 'mis-audiolibros') {
      navigate(`/mis-audiolibros/${usuarioId}`);
    } else if (origin === 'books-by-genre') {
      navigate(`/books-by-genre/${genre}/${usuarioId}`);
    } else {
      navigate('/');
    }
  };

  const {
    audiolibro,
    capitulos,
    currentChapterIndex,
    setCurrentChapterIndex,
    loading,
    error: fetchError,
    progress,
    setProgress,
  } = useFetchAudiolibro(audiolibroId, usuarioId, token);

  const [duration, setDuration] = useState(0);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isCurrentChapterLoaded, setIsCurrentChapterLoaded] = useState(false);
  const [isLoadingCurrentChapter, setIsLoadingCurrentChapter] = useState(false);
  const [volume, setVolume] = useState(1);
  const [error, setError] = useState('');
  const loadedChapters = useRef({});
  const soundRef = useRef(null);
  const isInitialLoad = useRef(true);
  const [isLoadingChapter, setIsLoadingChapter] = useState(false);
  const [accessibleProgressText, setAccessibleProgressText] = useState('');
  const [accessibleVolumeText, setAccessibleVolumeText] = useState('Volumen: 50%');

  const {
    formatTime,
    handlePlay,
    handlePause,
    handleSeek,
    increaseVolume,
    decreaseVolume,
    changeSpeed,
    handleSeekForward,
    handleSeekBackward,
  } = useAudioControls({
    soundRef,
    setIsPlaying,
    duration,
    setProgress,
    volume,
    setVolume,
    setPlaybackRate,
    setAccessibleProgressText,
    setAccessibleVolumeText,
  });

  const {
    updateProgress,
    saveProgress,
    handleBeforeUnload,
  } = useProgressHandlers({
    soundRef,
    progress,
    setProgress,
    usuarioId,
    audiolibroId,
    currentChapterIndex,
    token,
  });

  useLoadCurrentChapter({
    currentChapterIndex,
    audiolibroId,
    usuarioId,
    token,
    volume,
    playbackRate,
    progress,
    setDuration,
    setIsCurrentChapterLoaded,
    setIsLoadingCurrentChapter: setIsLoadingChapter,
    setError,
    updateProgress,
    saveProgress,
    setCurrentChapterIndex,
    capitulos,
    soundRef,
    loadedChapters,
    isInitialLoad,
  });

  usePreloadNextChapter({
    isCurrentChapterLoaded,
    currentChapterIndex,
    isLoadingCurrentChapter,
    capitulos,
    audiolibroId,
    usuarioId,
    token,
    loadedChapters,
  });

  useEffect(() => {
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [handleBeforeUnload]);

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex"
      role="main"
      aria-label="Reproductor de audiolibros"
    >
      {/* Sidebar */}
      <Sidebar
        capitulos={capitulos}
        currentChapterIndex={currentChapterIndex}
        setCurrentChapterIndex={(newIndex) => {
          setIsLoadingChapter(true);
          setCurrentChapterIndex(newIndex);
          if (soundRef.current) {
            soundRef.current.stop();
            soundRef.current.unload();
            soundRef.current = null;
            setIsPlaying(true);
          }
        }}
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
        audiolibroId={audiolibroId}
        aria-label="Menú de pistas"
        className={`fixed top-0 left-0 w-full h-full z-50 md:relative md:w-auto ${
          isSidebarOpen ? 'block' : 'hidden'
        }`}
      />
  
      {/* Contenido principal */}
      <div
        className="flex flex-col items-center justify-center w-full ml-auto mr-auto md:pl-64 relative"
        role="region"
        aria-label="Reproductor de audiolibros"
      >
        {/* Botón para abrir el sidebar en dispositivos móviles */}
        <button
          onClick={() => setIsSidebarOpen(true)}
          className="absolute top-6 left-6 md:hidden z-10 bg-purple-600 text-white p-2 rounded-full hover:bg-purple-700"
          aria-label="Abrir menú de pistas"
        >
          <Menu className="w-5 h-5" aria-hidden="true" />
        </button>
  
        <button
          onClick={handleGoBack}
          className="fixed top-6 left-72 z-20 bg-purple-600 text-white px-4 py-2 rounded-full hover:bg-purple-700 transition-all duration-300 shadow-lg 
                    sm:text-base text-sm sm:px-4 sm:py-2"
          aria-label="Volver a la página anterior"
        >
          {/* Icono */}
          <ArrowLeft className="w-5 h-5 inline-block mr-2 sm:mr-2" />
          {/* Texto (visible solo en pantallas grandes) */}
          <span className="hidden sm:inline">Volver</span>
        </button>
  
        {/* Título principal */}
        <h1 id="player-title" className="md:text-3xl text-2xl font-bold text-purple-400 mb-8">
          Reproduciendo Audiolibro
        </h1>
  
        {/* Estado de carga */}
        {loading && (
          <div className="flex flex-col items-center p-12" aria-live="polite">
            <div
              className="w-12 h-12 border-t-4 border-purple-400 rounded-full animate-spin mb-4"
              aria-hidden="true"
            ></div>
            <p className="text-purple-300">Cargando el audiolibro...</p>
          </div>
        )}
  
        {/* Mensaje de error */}
        {fetchError && (
          <div
            className="flex items-center justify-center p-6 bg-red-900/20 rounded-lg border border-red-500/50"
            role="alert"
          >
            <p className="text-red-500">{fetchError}</p>
          </div>
        )}
  
        {/* Reproductor de audiolibros */}
        {!loading && audiolibro && (
          <div
            className="w-full max-w-4xl bg-slate-800 rounded-lg p-6"
            aria-labelledby="player-title"
          >
            <h2 className="text-xl font-bold text-white mb-4">{audiolibro}</h2>
            <p className="text-sm text-gray-400 mb-4">Pista actual: {currentChapterIndex}</p>

            {isLoadingChapter && (
              <div
                className="flex flex-col items-center p-4"
                tabIndex={0}
              >
                <div className="w-8 h-8 border-t-4 border-purple-400 rounded-full animate-spin mb-2"></div>
                <p className="text-purple-300 text-sm">Cargando pista...</p>
              </div>
            )}

            <div aria-live="polite" className="sr-only">
              {isLoadingChapter ? 'Cargando pista, por favor espere' : ''}
            </div>

  
            {/* Barra de progreso */}
            <div className="w-full mb-4">
              <input
                type="range"
                min="0"
                max="100"
                value={(progress / duration) * 100 || 0}
                onChange={handleSeek}
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, #8b5cf6 ${(progress / duration) * 100}%, #374151 ${(progress / duration) * 100}%)`,
                }}
              />
              <span className="sr-only" aria-live="polite">
                {accessibleProgressText}
              </span>
            </div>
  
            {/* Indicadores de tiempo */}
            <div className="flex justify-between text-sm text-gray-400">
              <span>{formatTime(progress)}</span>
              <span>{formatTime(duration)}</span>
            </div>
  
            <div className="flex justify-between items-center mt-6 md:mt-4 flex-wrap gap-4">
              {/* Izquierda: Control de volumen */}
              <div className="flex items-center space-x-2">
                <VolumeIcon className="-mr-3 md:mr-0 w-5 h-5 text-gray-400" aria-hidden="true" />
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={volume}
                  onChange={(e) => {
                    const newVolume = parseFloat(e.target.value);
                    setVolume(newVolume);

                    const volumePercentage = Math.round(newVolume * 100);
                    setAccessibleVolumeText(`Volumen: ${volumePercentage}%`);

                    if (soundRef.current) {
                      soundRef.current.volume(newVolume);
                    }
                  }}
                  className="w-24 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, #8b5cf6 ${volume * 100}%, #374151 ${volume * 100}%)`,
                  }}
                  aria-label="Control de volumen"
                />
                {/* Texto accesible para el volumen */}
                <span
                  id="accessible-volume"
                  className="sr-only"
                  aria-live="polite"
                >
                  {accessibleVolumeText}
                </span>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => {
                      decreaseVolume();
                      const volumePercentage = Math.round(volume * 100);
                      setAccessibleVolumeText(`Volumen disminuido al ${volumePercentage}%`);
                    }}
                    className="px-2 py-1 bg-gray-700 text-white rounded-md hover:bg-gray-600"
                    aria-label="Bajar volumen"
                  >
                    -
                  </button>

                  <button
                    onClick={() => {
                      increaseVolume();
                      const volumePercentage = Math.round(volume * 100);
                      setAccessibleVolumeText(`Volumen aumentado al ${volumePercentage}%`);
                    }}
                    className="px-2 py-1 bg-gray-700 text-white rounded-md hover:bg-gray-600"
                    aria-label="Subir volumen"
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Centro: Botón de reproducir/pausar */}
              <div className="flex justify-center space-x-4 lg:hidden">
                {!isPlaying && (
                  <button
                    onClick={handlePlay}
                    className="px-4 py-2 border border-purple-600 text-purple-400 rounded-full bg-transparent hover:bg-purple-600 hover:text-white"
                    aria-label="Reproducir audio"
                  >
                    Reproducir
                  </button>
                )}
                {isPlaying && (
                  <button
                    onClick={handlePause}
                    className="px-4 py-2 border border-gray-600 text-gray-400 rounded-full bg-transparent hover:bg-gray-600 hover:text-white"
                    aria-label="Pausar audio"
                  >
                    Pausar
                  </button>
                )}
              </div>

              {/* Botones de avanzar/retroceder y reproducir/pausar en pantallas grandes */}
              <div className="hidden lg:flex flex-row items-center justify-center space-x-4">
                {/* Botón Retroceder */}
                <button
                  onClick={() => handleSeekBackward(30)}
                  className="px-3 py-1 text-white rounded-full hover:bg-gray-600"
                  aria-label="Retroceder 30 segundos"
                >
                  <MdReplay30 className="w-9 h-9" aria-hidden="true" />
                  <span className="sr-only">Retroceder 30 segundos</span>
                </button>

                {/* Botón Reproducir/Pausar */}
                <div className="flex justify-center space-x-4">
                  {!isPlaying && (
                    <button
                      onClick={handlePlay}
                      className="px-4 py-2 border border-purple-600 text-purple-400 rounded-full bg-transparent hover:bg-purple-600 hover:text-white"
                      aria-label="Reproducir audio"
                    >
                      Reproducir
                    </button>
                  )}
                  {isPlaying && (
                    <button
                      onClick={handlePause}
                      className="px-4 py-2 border border-gray-600 text-gray-400 rounded-full bg-transparent hover:bg-gray-600 hover:text-white"
                      aria-label="Pausar audio"
                    >
                      Pausar
                    </button>
                  )}
                </div>

                {/* Botón Avanzar */}
                <button
                  onClick={() => handleSeekForward(30)}
                  className="px-3 py-1 text-white rounded-full hover:bg-gray-600"
                  aria-label="Avanzar 30 segundos"
                >
                  <MdForward30 className="w-9 h-9" aria-hidden="true" />
                  <span className="sr-only">Avanzar 30 segundos</span>
                </button>
              </div>

              {/* Derecha: Controles de velocidad */}
              <div className="flex space-x-2 justify-center w-full md:w-auto mt-4 md:mt-0">
                <button
                  onClick={() => changeSpeed(0.75)}
                  className={`p-2 rounded-md ${playbackRate === 0.75 ? 'bg-purple-400' : 'bg-gray-700'}`}
                  aria-label="Cambiar velocidad de reproducción a 0.75x"
                >
                  0.75x
                </button>
                <button
                  onClick={() => changeSpeed(1)}
                  className={`px-4 rounded-md ${playbackRate === 1 ? 'bg-purple-400' : 'bg-gray-700'}`}
                  aria-label="Cambiar velocidad de reproducción a 1x"
                >
                  1x
                </button>
                <button
                  onClick={() => changeSpeed(1.5)}
                  className={`p-2 rounded-md ${playbackRate === 1.5 ? 'bg-purple-400' : 'bg-gray-700'}`}
                  aria-label="Cambiar velocidad de reproducción a 1.5x"
                >
                  1.5x
                </button>
                <button
                  onClick={() => changeSpeed(2)}
                  className={`px-4 rounded-md ${playbackRate === 2 ? 'bg-purple-400' : 'bg-gray-700'}`}
                  aria-label="Cambiar velocidad de reproducción a 2x"
                >
                  2x
                </button>
              </div>

              {/* Botones de avanzar/retroceder en pantallas pequeñas */}
              <div className="flex lg:hidden justify-center space-x-4 w-full mt-4">
                {/* Botón Retroceder */}
                <button
                  onClick={() => handleSeekBackward(30)}
                  className="px-3 py-1 text-white rounded-full hover:bg-gray-600"
                  aria-label="Retroceder 30 segundos"
                >
                  <MdReplay30 className="w-8 h-8" aria-hidden="true" />
                  <span className="sr-only">Retroceder 30 segundos</span>
                </button>

                {/* Botón Avanzar */}
                <button
                  onClick={() => handleSeekForward(30)}
                  className="px-3 py-1 text-white rounded-full hover:bg-gray-600"
                  aria-label="Avanzar 30 segundos"
                >
                  <MdForward30 className="w-8 h-8" aria-hidden="true" />
                  <span className="sr-only">Avanzar 30 segundos</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AudioPlayer;